import LFCButton from '_components/inputs/LFCButton';
import LFCFormRowGroup from '_components/layout/LFCFormRowGroup';
import ProgressBar from '_components/ProgressBar';
import {getLFCData, setLFCDataProc} from '_logics/LFCUtil';
import GenericTemplate from '_templates/GenericTemplate';
import axios, {CancelTokenSource} from 'axios';
import {useSnackbar} from 'notistack';
import {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';

import AddCircleIcon from '@mui/icons-material/AddCircle';
import {Box, Button, Grid, Tooltip} from '@mui/material';
import {
  DataGridPro,
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColumns,
  GridRenderCellParams,
  GridRowId,
  useGridApiRef
} from '@mui/x-data-grid-pro';

import FloorDeviceDialog from './_components/FloorDeviceDialog';
import FloorImageDialog from './_components/FloorImageDialog';
import FloorImageReorderDialog, {ImageItem} from './_components/FloorImageReorderDialog';
import FloorRenameDialog from './_components/FloorRenameDialog';
import LFCS3Image from '_components/LFCS3Image';

const SQL_ID_FLOOR_LIST = 8010;

const FloorPage = () => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const [startProcess, setStartProcess] = useState(false);

  const apiRef = useGridApiRef();
  const [dgColumns, setDgColumns] = useState<GridColumns>([]);
  const [dgRows, setDgRows] = useState<any[]>([]);
  const [selectionModel, setSelectionModel] = useState<GridRowId[]>([]);
  const [cacheKey, setCacheKey] = useState(() => Date.now());

  const cancelSource = useRef<CancelTokenSource | null>(null);
  const singleClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const doSearch = () => {
    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    setDgRows([]);
    setStartProcess(true);
    setCacheKey(Date.now());
    cancelSource.current = axios.CancelToken.source();
    getLFCData({
      snack: enqueueSnackbar,
      sql_id: SQL_ID_FLOOR_LIST,
      parameters: {},
      cancelToken: cancelSource.current.token,
      t
    })
      .then(datas => {
        if (datas.length === 0) {
          enqueueSnackbar(t('データがありません'), {variant: 'info'});
          return;
        }
        setDgRows(datas);
      })
      .finally(() => setStartProcess(false));
  };

  const doRename = () => {
    if (selectionModel.length !== 1) return;
    const row = dgRows.find(r => r.id === selectionModel[0]);
    if (!row) return;
    setPopFloorId(row.id);
    setPopFloorName(row.floor_name);
    setPopOpenFloorRename(true);
  };

  const doReorder = () => {
    if (selectionModel.length !== 1) return;
    const row = dgRows.find(r => r.id === selectionModel[0]);
    if (!row) return;
    const images: ImageItem[] = [1, 2, 3, 4, 5]
      .filter(n => row[`image${n}`])
      .map(n => ({
        imageNo: row[`image_no${n}`],
        imagePath: `${row[`image${n}`]}?${cacheKey}`,
        sortOrder: row[`sort_order${n}`] ?? n
      }));
    setPopFloorId(row.id);
    setPopFloorName(row.floor_name);
    setPopReorderImages(images);
    setPopOpenFloorReorder(true);
  };

  const doNewFloor = () => {
    setPopFloorId(0);
    setPopFloorName('');
    setPopImageNo(1);
    setPopOpenFloorImage(true);
  };

  const doAddImage = (floorId: number, floorName: string, imageNo: number) => {
    setPopFloorId(floorId);
    setPopFloorName(floorName);
    setPopImageNo(imageNo);
    setPopOpenFloorImage(true);
  };

  const doDeviceEdit = (
    floorId: number,
    floorName: string,
    imageNo: number,
    path: string,
    aspectRatio: number,
    iconRatio: number
  ) => {
    setPopFloorId(floorId);
    setPopFloorName(floorName);
    setPopImageNo(imageNo);
    setPopImagePath(path);
    setPopAspectRatio(aspectRatio);
    setPopIconRatio(iconRatio);
    setPopOpenFloorDevice(true);
  };

  const doDelete = (isOk: boolean) => {
    if (!isOk) return;

    if (cancelSource.current) cancelSource.current.cancel('req cancel');

    const selectionSet = new Set(selectionModel);
    const selectedFloors = dgRows
      .filter((r: any) => selectionSet.has(r.id))
      .map((r: any) => ({floorId: r.id, imageNo: -1}));

    if (selectedFloors.length === 0) {
      enqueueSnackbar(t('削除対象を選択してください'), {variant: 'error'});
      return;
    }

    setStartProcess(true);
    cancelSource.current = axios.CancelToken.source();
    setLFCDataProc({
      snack: enqueueSnackbar,
      name: '削除処理',
      invoke_name: 'DeleteFloorImage',
      parameters: {floors: selectedFloors},
      cancelToken: cancelSource.current.token,
      t
    })
      .then(() => doSearch())
      .catch(err => {
        console.error(err);
        enqueueSnackbar(t('削除に失敗しました'), {variant: 'error'});
      })
      .finally(() => setStartProcess(false));
  };

  useEffect(() => {
    setDgColumns([
      {field: 'id', headerName: '', hide: true},
      {
        field: 'floor_name',
        headerName: t('フロア名'),
        description: t('フロア名'),
        width: 200
      },
      ...[...Array(5)].map((_, i) => ({
        field: `image${i + 1}`,
        headerName: `${i + 1}`,
        description: `${t('画像')}${i + 1}`,
        renderCell: (prms: GridRenderCellParams) => {
          const imgPath = `${prms.value}?${cacheKey}`;

          return prms.value == null ? (
            <Grid container justifyContent="center">
              <Button
                color="primary"
                onClick={() => doAddImage(prms.row['id'], prms.row['floor_name'], i + 1)}
              >
                <AddCircleIcon />
              </Button>
            </Grid>
          ) : (
            <Tooltip title={t('クリック: 端末配置 / ダブルクリック: 画像変更')} followCursor>
              <LFCS3Image
                src={imgPath}
                width="100%"
                height="100%"
                alt=""
                onClick={() => {
                  if (singleClickTimer.current) clearTimeout(singleClickTimer.current);
                  singleClickTimer.current = setTimeout(() => {
                    singleClickTimer.current = null;
                    doDeviceEdit(
                      prms.row['id'],
                      prms.row['floor_name'],
                      prms.row[`image_no${i + 1}`],
                      imgPath,
                      prms.row[`aspect_ratio${i + 1}`],
                      prms.row[`icon_ratio${i + 1}`]
                    );
                  }, 300);
                }}
                onDoubleClick={() => {
                  if (singleClickTimer.current) {
                    clearTimeout(singleClickTimer.current);
                    singleClickTimer.current = null;
                  }
                  doAddImage(prms.row['id'], prms.row['floor_name'], prms.row[`image_no${i + 1}`]);
                }}
                style={{cursor: 'pointer', objectFit: 'fill'}}
              />
            </Tooltip>
          );
        },
        width: 200
      }))
    ]);

    doSearch();

    return () => {
      if (cancelSource.current) cancelSource.current.cancel('req cancel');
    };
  }, []);

  const [popOpenFloorImage, setPopOpenFloorImage] = useState(false);
  const [popFloorId, setPopFloorId] = useState(0);
  const [popFloorName, setPopFloorName] = useState('');
  const [popImageNo, setPopImageNo] = useState(1);
  const [popImagePath, setPopImagePath] = useState('');
  const [popAspectRatio, setPopAspectRatio] = useState(0);
  const [popIconRatio, setPopIconRatio] = useState(1);

  const handleCloseFloorImage = (isRefresh: boolean) => {
    setPopOpenFloorImage(false);
    if (isRefresh) doSearch();
  };

  const [popOpenFloorDevice, setPopOpenFloorDevice] = useState(false);
  const handleCloseFloorDevice = (isRefresh: boolean) => {
    setPopOpenFloorDevice(false);
    if (isRefresh) doSearch();
  };

  const [popOpenFloorRename, setPopOpenFloorRename] = useState(false);
  const [popOpenFloorReorder, setPopOpenFloorReorder] = useState(false);
  const [popReorderImages, setPopReorderImages] = useState<ImageItem[]>([]);

  return (
    <GenericTemplate title={t('フロア登録')}>
      <ProgressBar startProcess={startProcess} />
      <Grid
        container
        spacing={2}
        rowSpacing={2}
        alignItems="flex-start"
        justifyContent="flex-start"
      >
        <Grid item xs={12}>
          <LFCButton color="primary" onClick={doSearch}>
            {t('message.最新の情報に更新')}
          </LFCButton>
        </Grid>
        <Grid item xs={12}>
          <Box style={{height: '75vh'}}>
            <DataGridPro
              apiRef={apiRef}
              columns={dgColumns}
              rows={dgRows}
              density="compact"
              onSelectionModelChange={setSelectionModel}
              selectionModel={selectionModel}
              showCellRightBorder
              showColumnRightBorder
              checkboxSelection
              disableSelectionOnClick
              initialState={{
                pinnedColumns: {
                  left: [GRID_CHECKBOX_SELECTION_COL_DEF.field, 'floor_name'],
                  right: ['actions']
                }
              }}
              rowHeight={100}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <LFCFormRowGroup>
            <LFCButton color="primary" onClick={doRename} disabled={selectionModel.length !== 1}>
              {t('名称変更')}
            </LFCButton>
            <LFCButton color="primary" onClick={doReorder} disabled={selectionModel.length !== 1}>
              {t('画像並び替え')}
            </LFCButton>
            <LFCButton color="primary" onClick={doNewFloor}>
              {t('新規')}
            </LFCButton>
            <LFCButton
              color="secondary"
              onClick={doDelete}
              confirmMessage={t('削除します。よろしいですか？')}
            >
              {t('削除')}
            </LFCButton>
          </LFCFormRowGroup>
        </Grid>
      </Grid>

      <FloorImageDialog
        open={popOpenFloorImage}
        onClose={handleCloseFloorImage}
        floorId={popFloorId}
        floorName={popFloorName}
        imageNo={popImageNo}
      />

      <FloorDeviceDialog
        open={popOpenFloorDevice}
        onClose={handleCloseFloorDevice}
        floorId={popFloorId}
        floorName={popFloorName}
        imageNo={popImageNo}
        imagePath={popImagePath}
        aspectRatio={popAspectRatio}
        iconRatio={popIconRatio}
      />

      <FloorRenameDialog
        open={popOpenFloorRename}
        onClose={isRefresh => {
          setPopOpenFloorRename(false);
          if (isRefresh) doSearch();
        }}
        floorId={popFloorId}
        floorName={popFloorName}
      />

      <FloorImageReorderDialog
        open={popOpenFloorReorder}
        onClose={isRefresh => {
          setPopOpenFloorReorder(false);
          if (isRefresh) doSearch();
        }}
        floorId={popFloorId}
        floorName={popFloorName}
        images={popReorderImages}
      />
    </GenericTemplate>
  );
};

export default FloorPage;
